import { routeUrl } from "@/app/routes";

/**
 * WHICH crumbs a pathname produces, with no React in it.
 *
 * Split out of `topbar-breadcrumb.tsx` on 2026-08-20, when adding the leave
 * and attendance titles pushed that file past the 250 line limit. The seam was
 * already there: this half is a pure function over a path and a slug, it is
 * what the tests exercise, and it changes every time a route is added, while
 * the component below it has not changed in months.
 */

// Top-level pages (no workspace context).
const TOP_LEVEL_TITLES: Record<string, string> = {
  "/design-system": "Design system",
  "/settings": "Settings",
  "/settings/profile": "Settings",
  "/settings/tokens": "Settings",
  "/settings/mcp": "Settings",
  "/settings/ai-connections": "Settings",
  "/settings/account": "Settings",
  "/settings/billing": "Settings",
  // No entry for /account/billing/redeem on purpose: it would render a
  // nested "Billing / Redeem a code" trail, and that screen already carries
  // its own page heading. /pricing is likewise absent, since it is a public
  // sales page reachable signed-out, not a place in the app's navigation.
  "/profile": "Profile",
};

// Workspace-scoped pages, keyed by the sub-path under `/<slug>`. Also the
// source of parent crumbs: a page's parent is the longest titled prefix of
// its sub-path ("/pulse-surveys/new" -> "/pulse-surveys").
const WORKSPACE_SUBPATH_TITLES: Record<string, string> = {
  "": "Home",
  "/": "Home",
  "/people": "People",
  "/my-team": "My team",
  "/org-chart": "Org chart",
  "/todos": "Todos",
  "/admin/todos": "All workspace todos",
  "/goals": "Goals",
  "/feedback": "Feedback",
  "/admin/feedback": "All workspace feedback",
  "/kudos": "Kudos",
  "/compensation": "Compensation",
  "/expenses": "Expenses",
  "/expenses/advances": "Advances",
  "/expenses/travel": "Travel",
  "/one-on-ones": "1:1s",
  "/performance-reviews": "Reviews",
  // No "/performance-reviews/cycles" title on purpose: parent resolution picks
  // the longest titled prefix, so titling it would make round subpages read
  // "Review rounds" instead of linking back to the "Reviews" section.
  "/performance-reviews/templates": "Question sets",
  "/performance-reviews/insights": "Insights",
  "/performance-reviews/appraisal-schedules": "Appraisal schedules",
  "/notifications": "Notifications",
  "/notifications/preferences": "Notification preferences",
  "/my-pulse": "My surveys",
  "/my-pulse/answered": "Answered",
  "/pulse-surveys": "Surveys",
  "/pulse-surveys/new": "New survey",
  "/pulse-surveys/answer-threads": "Conversations",
  "/insights": "Insights",
  "/settings/health-config": "Workspace health config",
  "/actions": "Workspace actions",
  "/actions/my": "My actions",
  "/actions/updates": "Action updates",
  "/settings": "Workspace settings",
  "/settings/company": "Company",
  // Attendance and leave (M16, M17). Absent until 2026-08-20, which is why
  // every page in both modules showed the workspace name and nothing else,
  // and why all eight of them had grown a visible <h1> of their own while the
  // other 42 route pages carry an `sr-only` one.
  //
  // Titles are self-describing rather than copies of the sidebar labels: the
  // sidebar says "Team" under both Attendance and Leave, and a crumb reading
  // "Acme / Team" names neither. Same reason "/actions" reads "Workspace
  // actions" here and something shorter in the nav.
  //
  // `/attendance/requests` and `/attendance/board` are deliberately absent:
  // both are redirects, so no reader ever sees a crumb for them.
  "/attendance": "My attendance",
  "/attendance/team": "Team attendance",
  "/settings/attendance": "Attendance",
  // Payroll (M40, PAY-005). Under /payroll rather than /settings because
  // HRMS-PAY-023 adds the member-facing payslips page as a sibling; the setup
  // page is the admin half of the same section, not a workspace setting.
  "/payroll/setup": "Payroll setup",
  "/payroll/salaries": "Salaries",
  "/payroll/inputs": "This month's extras",
  "/payroll/recoveries": "Recoveries",
  "/payroll/settlements": "Settlements",
  "/payroll/documents": "Compliance documents",
  "/payroll/tax-declarations": "Tax declarations",
  // Redirects to Tax declarations?tab=calculator. Titled so a stale paint
  // or a bookmark still names the destination.
  "/payroll/tax-calculator": "Tax calculator",
  "/payroll/benefits": "Benefits",
  "/recruitment": "Recruitment",
  "/recruitment/offers": "Offers",
  // HRMS-REC-UX P4: the five tabs became routes, so each needs a crumb of its
  // own. They read as siblings of the jobs list because that is what they are:
  // workspace-wide, not per job.
  "/recruitment/requisitions": "Requisitions",
  "/recruitment/staffing-plans": "Staffing plans",
  "/recruitment/letters": "Appointment letters",
  "/recruitment/referrals": "Referrals",
  "/recruitment/interviews": "Interviews",
  // The member half (HRMS-PAY-023). No entry for either print route: both
  // render outside the app shell, so there is no topbar on them to put a crumb
  // in.
  "/payroll/payslips": "My payslips",
  // ESS-01. A sibling of My payslips rather than a child: the sidebar lists
  // the two side by side, and "My payslips / My pay details" would claim a
  // hierarchy that does not exist.
  "/payroll/my-details": "My pay details",
  // Payroll depth F-08 and K-05.
  "/payroll/variable-pay": "Variable pay",
  "/payroll/parallel-run": "Parallel run",
  // Payroll depth L-06, the member money audit.
  "/payroll/money-audit": "Money audit",
  // HRMS-PAY-022. The wizard itself has no map entry: `/payroll/runs/<id>` is
  // a dynamic route, so `resolveSubTitle` answers it below and the parent
  // resolution turns the trail into "Payroll runs / Run".
  "/payroll/runs": "Payroll runs",
  "/leave": "My leave",
  "/leave/history": "Leave history",
  "/leave/team": "Team leave",
  "/leave/balances": "Leave balances",
  "/settings/leave": "Leave",
  "/settings/hr": "HR settings",
  "/approvals": "Approvals",
};

/**
 * Sub-paths that are SIBLINGS of a titled prefix, not children of it.
 *
 * Parent resolution takes the longest titled prefix, which is right for
 * `/leave/history` (reached only from a link on My leave, and reads "My leave
 * / Leave history"). It is wrong for `/leave/team`, which the sidebar lists
 * beside My leave rather than under it: the URL nests, the navigation does
 * not, and "My leave / Team leave" would claim a hierarchy that does not
 * exist and link a manager back to a page about themselves.
 *
 * The URL shape is an accident of grouping a module under one path. This set
 * is where that accident stops being load-bearing.
 */
const SIBLING_SUBPATHS = new Set(["/attendance/team", "/leave/team", "/leave/balances"]);

interface SubTitle {
  title: string;
  /// Set on member-facing pages whose section root is admin-gated; a parent
  /// crumb there would be a dead link for most viewers.
  suppressParent?: boolean;
}

/// Title for a workspace sub-path: exact map hit first, then dynamic detail
/// routes fall back to a section-level title.
function resolveSubTitle(sub: string): SubTitle | undefined {
  const exact = WORKSPACE_SUBPATH_TITLES[sub];
  if (exact) return { title: exact, suppressParent: SIBLING_SUBPATHS.has(sub) };
  if (sub.startsWith("/payroll/runs/")) return { title: "Run" };
  if (sub.startsWith("/payroll/settlements/")) return { title: "Worksheet" };
  if (sub.startsWith("/payroll/documents/")) return { title: "Document run" };
  // HRMS-REC-011. The parent resolution turns the trail into
  // "Recruitment / Applicant", and the route replaces the trailing crumb with
  // the candidate's name once the detail query answers.
  if (sub.startsWith("/recruitment/applicants/")) return { title: "Applicant" };
  // HRMS-REC-UX P4.5. Dynamic like the applicant crumb above, and for the same
  // reason: the job's title is not known until its query answers, so the route
  // replaces this trailing crumb with the real name.
  if (sub.startsWith("/recruitment/openings/")) return { title: "Job" };
  if (sub.startsWith("/payroll/variable-pay/") && sub.includes("/batches/")) {
    return { title: "Bonus batch" };
  }
  if (sub.startsWith("/payroll/variable-pay/")) return { title: "Bonus plan" };
  if (sub.startsWith("/people/") && sub.endsWith("/edit")) return { title: "Edit employee" };
  // HRMS FND-011's HR profile, a flat sibling of the edit route rather than a
  // child of the person page. Without this arm it fell through to the "1:1
  // board" fallback below, so the whole HR profile read "People / 1:1 board".
  if (sub.startsWith("/people/") && sub.endsWith("/hr")) return { title: "HR profile" };
  if (sub.startsWith("/people/")) return { title: "1:1 board" };
  if (sub.startsWith("/todos/")) return { title: "Todo" };
  if (sub.startsWith("/goals/")) return { title: "Goal" };
  if (sub.startsWith("/feedback/")) return { title: "Feedback" };
  if (sub.startsWith("/kudos/")) return { title: "Kudos" };
  // HRMS-EXP-004. `/expenses/<claimId>` reads "Expenses / Claim". Advances and
  // travel (EXP-012, EXP-021) are static siblings, so they will answer from
  // the exact map above before this arm ever sees them.
  if (sub.startsWith("/expenses/")) return { title: "Claim" };
  if (sub.startsWith("/one-on-ones/")) return { title: "1:1s" };
  if (sub.startsWith("/performance-reviews/templates/")) {
    return { title: sub.endsWith("/view") ? "Template" : "Edit template" };
  }
  if (sub.startsWith("/performance-reviews/cycles/")) {
    if (sub.endsWith("/manager-ratings")) return { title: "Manager ratings" };
    if (sub.endsWith("/self-review")) return { title: "Self-review" };
    if (sub.endsWith("/peer-review")) return { title: "Peer review" };
    if (sub.endsWith("/manager-review")) return { title: "Manager review" };
    if (sub.endsWith("/results")) return { title: "Results" };
    return { title: "Review round" };
  }
  if (sub.startsWith("/performance-reviews/")) return { title: "Review" };
  if (sub.startsWith("/pulse-surveys/answer-threads/")) return { title: "Conversation" };
  if (sub.startsWith("/my-pulse/conversations/")) return { title: "Conversation" };
  if (sub.startsWith("/pulse-surveys/me/")) return { title: "Survey", suppressParent: true };
  if (sub.startsWith("/pulse-surveys/") && sub.endsWith("/edit")) return { title: "Edit survey" };
  if (sub.startsWith("/pulse-surveys/") && sub.endsWith("/insights")) return { title: "Results" };
  if (sub.startsWith("/pulse-surveys/")) return { title: "Survey" };
  return undefined;
}

export interface Crumb {
  label: string;
  href?: string;
}

/// Longest titled prefix of `sub` (excluding `sub` itself), as a linked crumb.
function parentCrumb(sub: string, slug: string): Crumb | null {
  let best: string | null = null;
  for (const key of Object.keys(WORKSPACE_SUBPATH_TITLES)) {
    if (
      key.length > 1 &&
      sub.startsWith(`${key}/`) &&
      (best === null || key.length > best.length)
    ) {
      best = key;
    }
  }
  if (best === null) return null;
  // Todos default sort lives in the URL; link back with it intact.
  const href =
    best === "/todos"
      ? `${routeUrl.todos.list(slug)}?sortBy=sortOrder&sortDir=asc`
      : `/${slug}${best}`;
  const bestTitle = WORKSPACE_SUBPATH_TITLES[best];
  return bestTitle ? { label: bestTitle, href } : null;
}

/// Middle crumbs between the workspace name and any trailing entity crumb.
/// With a trailing crumb the singular fallback title ("Goal", "Feedback") is
/// dropped in favour of the linked section: `workspace / Goals / Q3 revenue`.
export function resolveCrumbs(pathname: string, slug: string, hasTrailing: boolean): Crumb[] {
  if (pathname === "/") return [{ label: "Home" }];
  const topLevel = TOP_LEVEL_TITLES[pathname];
  if (topLevel) return [{ label: topLevel }];

  // Any remaining pathname is workspace-scoped: first segment is the slug,
  // everything after is the sub-path the title map is keyed on.
  const wsMatch = pathname.match(/^\/[^/]+(\/.*)?$/);
  if (!wsMatch) return [];
  const sub = wsMatch[1] ?? "";
  const page = resolveSubTitle(sub);

  if (hasTrailing) {
    const parent = page?.suppressParent ? null : parentCrumb(sub, slug);
    if (parent) return [parent];
    return page ? [{ label: page.title }] : [];
  }

  if (!page) return [];
  const parent = page.suppressParent ? null : parentCrumb(sub, slug);
  if (parent && parent.label !== page.title) return [parent, { label: page.title }];
  return [{ label: page.title }];
}
